import { useEffect, useSyncExternalStore } from 'react'

import { api } from '~/api/client'

/** The four things that are waiting on you, for the badges on the bar. */
export interface Waiting {
  /** Friend requests somebody has sent you and you have not answered. */
  friends: number
  /**
   * Gigs the briefing would still ask you about. Zero for anybody but the editor.
   *
   * The real number even in normie mode, and that is not an oversight. It was masked here
   * for one commit, which took the count away from the banner that needs it — the switch
   * hides the *button*, and the banner's whole job is to say what you are not being shown.
   * `Shell` decides what to draw with it.
   */
  briefing: number
  /**
   * Artist rows that resolved as acts and are not — the `/tidy` queue. Zero for anybody but
   * the editor, because it is their queue and nobody else may act on it.
   *
   * The real number in normie mode too, for the same reason as `briefing`: the switch hides
   * the button and the strip's job is to say what is being hidden.
   */
  tidy: number
  /**
   * Unread notices, for the bell.
   *
   * Counted on this endpoint rather than fetched from `/notices`, because the badge is on
   * every page and the panel is opened on almost none of them — asking for the list to
   * learn the number would fetch fifty rows to render one digit. It is three index seeks
   * against a table with a handful of rows in it, so it is the cheapest of the four by a
   * wide margin.
   */
  notices: number
  /**
   * Whether the numbers above are an answer or a starting position.
   *
   * Needed because zero is a real count. The briefing header shows its own `outstanding`
   * until this hook has spoken, and without a flag "not asked yet" and "nothing left to
   * decide" are the same value — so finishing the pile would show the stale figure the page
   * loaded with, which is the bug this whole change is about.
   */
  loaded: boolean
}

const NOTHING: Waiting = { friends: 0, briefing: 0, tidy: 0, notices: 0, loaded: false }

/**
 * Fired by anything that answers for a gig, so whatever shows a count can re-ask.
 *
 * An event rather than a call into the store below: the writers — the briefing, `/tidy`, the
 * spellings page, the bell — are routes and panels that know they changed something and
 * nothing about who is drawing a count, and the store only holds anything while somebody is.
 */
export const CHANGED = 'lg:waiting'

/**
 * How old the count may be before coming back to the tab asks for it again, in milliseconds.
 *
 * Exported so a test can step past it rather than restate it.
 */
export const FRESH_FOR = 60_000

/** Say the counts have moved. Cheap enough to call after every decision. */
export function waitingChanged(): void {
  window.dispatchEvent(new Event(CHANGED))
}

/*
 * One count for the page, and one request for it at a time.
 *
 * The bar and the briefing's heading each had a `useWaiting` of their own, each listening
 * for `CHANGED`, so every decision sent `/waiting` twice — and the server answers `/waiting`
 * and every write under one lock, so the second copy sat in front of the next press's write.
 * The count is held here, at module level, because its two readers have no common owner:
 * the bar is above every route and the briefing is inside one.
 *
 * `asking` is the request in flight. A `CHANGED` that arrives while it is out does not send
 * another: it marks the answer `stale`, and one more request goes when this one lands. It
 * cannot simply join the one in flight, which may have been answered before the write that
 * fired the event — but any number of events during one request need only one request after
 * it. Everything else that asks — a hook mounting, the tab coming back — joins the one in
 * flight, because it asked for nothing that request will not answer.
 *
 * `round` moves whenever what is held is thrown away — another account, or nobody left
 * watching — so an answer to a request sent before that is dropped rather than painted over
 * the next account's, and its `finally` does not clear a request it no longer owns.
 *
 * `sent` is when the last request went out, and coming back to the tab asks only once that
 * is `FRESH_FOR` behind. `/waiting` is answered under the same lock as every write, so a
 * request on every focus put that wait in front of the first click after each return. What
 * it catches on a return is somebody else's change — an ingest run, a friend request — and
 * those may wait a while; one return fires both focus and `visibilitychange`, and the
 * second finds the first in flight or fresh. Writes are not held to it: `CHANGED` is news
 * from this tab, and goes through `moved` exactly as before.
 */
let held: { account: number | null; count: Waiting } = { account: null, count: NOTHING }
let asking: Promise<void> | null = null
let stale = false
let round = 0
let sent = -Infinity
let watching = 0
const readers = new Set<() => void>()

function show(count: Waiting): void {
  held = { ...held, count }
  for (const tell of readers) tell()
}

function forget(account: number | null): void {
  round += 1
  asking = null
  stale = false
  // What was sent was for a count just thrown away, so it says nothing about how old the
  // next one is.
  sent = -Infinity
  held = { account, count: NOTHING }
  for (const tell of readers) tell()
}

function ask(): void {
  if (held.account === null || asking) return
  const mine = round
  // Stamped on sending, not on the answer: the server reads the count no earlier than this,
  // so an answer is never older than its stamp says. A request that fails is stamped too,
  // though the count it leaves on screen is older, so that a server that cannot answer is
  // not asked again on every return to the tab.
  sent = Date.now()
  asking = api
    .waiting()
    .then(
      answer => {
        if (mine === round) show({ ...answer, loaded: true })
      },
      // A badge is not worth a visible failure. If the count cannot be fetched the bar keeps
      // whatever it last said, and claims nothing it was never told.
      () => undefined,
    )
    .finally(() => {
      if (mine !== round) return
      asking = null
      if (stale) {
        stale = false
        ask()
      }
    })
}

function moved(): void {
  if (asking) stale = true
  else ask()
}

function again(): void {
  if (document.visibilityState !== 'visible') return
  /*
   * The wall clock rather than `performance.now()`, because the case that most needs a fresh
   * count is a laptop lid opened the next morning, and the monotonic clock is not promised to
   * advance while the machine sleeps. The cost is that the wall clock can be set back: an age
   * below zero is read as unknown, and asks, rather than as fresh until the clock catches up.
   */
  const age = Date.now() - sent
  if (age >= 0 && age < FRESH_FOR) return
  ask()
}

/** Start watching for `account`, and hand back how to stop. The last to stop clears it all. */
function watch(account: number): () => void {
  if (held.account !== account) forget(account)
  watching += 1
  if (watching === 1) {
    document.addEventListener('visibilitychange', again)
    window.addEventListener('focus', again)
    window.addEventListener(CHANGED, moved)
  }
  ask()
  return () => {
    watching -= 1
    if (watching > 0) return
    document.removeEventListener('visibilitychange', again)
    window.removeEventListener('focus', again)
    window.removeEventListener(CHANGED, moved)
    forget(null)
  }
}

function subscribe(tell: () => void): () => void {
  readers.add(tell)
  return () => {
    readers.delete(tell)
  }
}

/**
 * What is waiting on you, for the bar.
 *
 * Its own tiny hook rather than part of any panel, because the bar is on every page and the
 * panels are each on one. Keyed on the account so signing in as somebody else does not
 * inherit the last person's counts — the same rule the calendar's rows follow. Every caller
 * reads the one count above, so the bar's badge and the briefing's heading cannot disagree on
 * screen, and a decision costs one request however many places draw the number.
 *
 * One request for all four, from `/waiting`. Separate ones would be round trips for integers,
 * and the briefing half cannot come from `/entries` — that is the calendar's payload, so a
 * badge on the front page would be paying for the whole calendar to learn a number.
 *
 * Re-asked on three things, and the third is the one that was missing.
 *
 * Mounting, and coming back to the tab once the count is `FRESH_FOR` old, catch new arrivals
 * — ingest runs on a timer, so polling would ask far more often than anything changes, and a
 * push channel is a service, a credential and a subscription table for a badge. Coming back
 * is held to the age because it is not free: see `sent` above.
 *
 * Neither of those fires while you are working *through* the briefing, which is exactly when
 * the number is changing and exactly when somebody is looking at it. So whatever changes
 * something says so, on `CHANGED` above, and the count is asked for again.
 */
export function useWaiting(accountId: number | null): Waiting {
  useEffect(() => (accountId === null ? undefined : watch(accountId)), [accountId])
  return useSyncExternalStore(subscribe, () =>
    accountId !== null && held.account === accountId ? held.count : NOTHING,
  )
}
